import { getEntityTypeFromPath } from 'app/infrastructure/store/entity';
import { getValue } from 'app/infrastructure/ui/form/selectors';
import { StoreData } from 'app/types-shared';
import {
  getCaseData,
  getEntityContext
} from 'app/infrastructure/ui/renderer/resolvers';
import { CaseState } from 'app/area/case/types-store';
import { ConnectedComponentProps } from 'app/infrastructure/ui/types-uischema';
import { getStorePath } from 'app/infrastructure/store/path';
import { getChildEntities } from 'app/infrastructure/store/query';

/**
 * Provide aggregated count for a child table of Case
 */

export interface ChildCountMapping {
  /**
   * The name to use for the aggregation result
   */
  propName: string;
  /**
   * Only select rows where this column has a value.
   * If not defined, count all entities in the target table
   */
  dataTag?: string;
  /**
   * This is the data path with optional ids to match for each entity.
   * The ancestors with no specified id will be matched by using the component context.
   */
  entityPath: { entityType: string; id?: number }[];
}

/**
 * Provide aggregated count for a child table of Case or Draft
 */

export const childEntityCount = (propMappings: ChildCountMapping[]) => (
  caseState: CaseState,
  uiEngineProps: ConnectedComponentProps
) => {
  const propObject = {};

  propMappings.forEach(prop => {
    const entityTypePath = prop.entityPath.map(d => d.entityType);
    const caseData: StoreData = getCaseData(caseState, uiEngineProps);
    const entityContext = getEntityContext(caseState, uiEngineProps);
    const storePath = getStorePath(entityTypePath, entityContext.path, true);

    const path = prop.entityPath
      .map(d => {
        const pathItem = storePath.find(p => !!p[d.entityType]);
        return pathItem || (d.id && { [d.entityType]: d.id });
      })
      .filter(d => d);

    const entityType = getEntityTypeFromPath(entityTypePath);
    const allRows = getChildEntities(caseData, path, entityType);
    const filteredRows =
      prop.dataTag && allRows.filter(e => !!getValue(e, prop.dataTag).value);

    const result = filteredRows || allRows;
    propObject[prop.propName] = {
      count: result.length,
      propName: prop.propName
    };
  });

  return propObject;
};

export interface ChildEntityCountShape {
  count: number;
  propName: string;
}
